import type { GitRevisionsFilter } from "@ext/git/actions/Revisions/model/GitRevisionsFilter";
import type { DiffTree } from "@ext/git/core/GitDiffItemCreator/RevisionDiffPresenter";
import type GitVersionData from "@ext/git/core/model/GitVersionData";
import { createStore } from "zustand";
import { shallow } from "zustand/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";

export type RevisionArticleFilter = {
	name: string;
	path: string;
	pathname: string;
};

type Filters = Omit<GitRevisionsFilter, "pathspecs"> & {
	articles?: RevisionArticleFilter[];
};

type RevisionsCompare = {
	from: GitVersionData;
	to: GitVersionData;
};

interface RevisionCatalogState {
	status: "comparing" | "default";
	revision: GitVersionData;
	diffTree: DiffTree;
	compareDiffTree: DiffTree;
	revisions: GitVersionData[];
	reachedFirstCommit: boolean;
	filter: Filters;
	revisionsCompare: RevisionsCompare;
	scrollY: number;
	catalogName: string;
	openingRevision: string;
}

interface RevisionCatalogStore extends RevisionCatalogState {
	setFilter: (filter: Filters) => void;
	setRevision: (revision: string) => void;
	setDiffTree: (diffTree: DiffTree) => void;
	setCompareDiffTree: (compareDiffTree: DiffTree) => void;
	setRevisions: (revisions: GitVersionData[]) => void;
	setReachedFirstCommit: (reachedFirstCommit: boolean) => void;
	setRevisionsCompare: (revisionsCompare: RevisionsCompare) => void;
	setStatus: (status: "comparing" | "default") => void;
	setScrollY: (scrollY: number) => void;
	setCatalogName: (catalogName: string) => void;
	setOpeningRevision: (openingRevision: string) => void;
	reset: () => void;
}

const initialState: RevisionCatalogState = {
	status: "default",
	revision: null,
	diffTree: null,
	compareDiffTree: null,
	revisions: null,
	reachedFirstCommit: false,
	filter: null,
	revisionsCompare: null,
	scrollY: 0,
	catalogName: null,
	openingRevision: null,
};

export const revisionCatalogStore = createStore<RevisionCatalogStore>((set, get) => ({
	...initialState,

	setFilter: (filter) => set({ filter }),
	setRevision: (oid) => {
		const revisionData = get().revisions?.find((revision) => revision.oid === oid);
		if (!revisionData) return;
		set({ revision: revisionData });
	},
	setDiffTree: (diffTree) => set({ diffTree }),
	setCompareDiffTree: (compareDiffTree) => set({ compareDiffTree }),
	setRevisions: (revisions) => set({ revisions }),
	setReachedFirstCommit: (reachedFirstCommit) => set({ reachedFirstCommit }),
	setRevisionsCompare: (revisionsCompare) => set({ revisionsCompare }),
	setStatus: (status) => set({ status }),
	setScrollY: (scrollY) => set({ scrollY }),
	setCatalogName: (catalogName) => set({ catalogName }),
	setOpeningRevision: (openingRevision) => set({ openingRevision }),
	reset: () => set({ ...initialState, catalogName: get().catalogName }),
}));

export const useRevisionCatalogStore = <T>(selector: (state: RevisionCatalogStore) => T): T => {
	return useStoreWithEqualityFn(revisionCatalogStore, selector, shallow);
};
